import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import useTreatmentPlanV3ReferralPageState from './useTreatmentPlanV3ReferralPageState';

import type { AppDispatch } from '../../../../../../store';
import type { ReferralPayload } from '../../../../../common';
import type {
  TreatmentPlanV3ReferralsDependencies,
  UseTreatmentPlanV3ReferralsResult,
} from './types';

export default function useTreatmentPlanV3Referrals() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const pageState = useTreatmentPlanV3ReferralPageState();

  return useTreatmentPlanV3ReferralsWithDependencies({
    actions: treatmentPlanV3Actions,
    dispatch,
    getAccessTokenSilently,
    treatmentPlanId,
    ...pageState,
  });
}

export function useTreatmentPlanV3ReferralsWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  treatmentPlanId,
  referrals,
  isLoading,
  hasLoaded,
  hasLoadError,
  isSaving,
}: TreatmentPlanV3ReferralsDependencies): UseTreatmentPlanV3ReferralsResult {
  const create = useCallback(
    async (data: ReferralPayload) => {
      if (!treatmentPlanId) return undefined;
      const result = await dispatch(
        actions.createTreatmentPlanV3Referral({ treatmentPlanId, data, getAccessTokenSilently }),
      );
      return actions.createTreatmentPlanV3Referral.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const update = useCallback(
    async (referralId: number, data: ReferralPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3Referral({
          treatmentPlanId,
          referralId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3Referral.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const remove = useCallback(
    async (referralId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.deleteTreatmentPlanV3Referral({
          treatmentPlanId,
          referralId,
          getAccessTokenSilently,
        }),
      );
      return actions.deleteTreatmentPlanV3Referral.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return { referrals, isLoading, hasLoaded, hasLoadError, isSaving, create, update, remove };
}
